import React, { useState, useEffect } from 'react';
import { FaClock, FaTimes } from 'react-icons/fa';
import { Theme } from '../../lib/theme';

interface TrialBannerProps {
  trialEndDate: string | null; // ISO date string or null
  onDismiss?: () => void;
}

export const TrialBanner: React.FC<TrialBannerProps> = ({ trialEndDate, onDismiss }) => {
  const [daysRemaining, setDaysRemaining] = useState<number>(0);
  const [hoursRemaining, setHoursRemaining] = useState<number>(0);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (!trialEndDate) return;

    const calculateTimeRemaining = () => {
      const now = new Date();
      const endDate = new Date(trialEndDate);
      const timeDiff = endDate.getTime() - now.getTime();

      if (timeDiff <= 0) {
        setDaysRemaining(0);
        setHoursRemaining(0);
        return;
      }

      const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

      setDaysRemaining(days);
      setHoursRemaining(hours);
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [trialEndDate]);

  if (!trialEndDate || isDismissed || (daysRemaining === 0 && hoursRemaining === 0)) {
    return null;
  }

  const handleDismiss = () => {
    setIsDismissed(true);
    if (onDismiss) onDismiss();
  };

  return (
    <div
      className="relative w-full px-4 py-3 rounded-xl shadow-md flex items-center justify-between mb-4 animate-fade-in"
      style={{
        backgroundColor: '#FFF9E6',
        border: `1px solid #FFD700`,
      }}
    >
      <div className="flex items-center gap-3 flex-1">
        <div
          className="flex items-center justify-center w-10 h-10 rounded-full"
          style={{ backgroundColor: '#FFD700' }}
        >
          <FaClock className="text-white text-lg" />
        </div>
        
        <div className="flex flex-col">
          <p className="text-sm font-semibold" style={{ color: Theme.colors.text.strong }}>
            🎉 Free Trial Active
          </p>
          <p className="text-xs" style={{ color: Theme.colors.text.weak }}>
            {daysRemaining > 0 ? (
              <>
                <span className="font-bold text-orange-600">{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</span>
                {hoursRemaining > 0 && ` and ${hoursRemaining} hour${hoursRemaining !== 1 ? 's' : ''}`} remaining
              </>
            ) : (
              <>
                <span className="font-bold text-orange-600">{hoursRemaining} hour{hoursRemaining !== 1 ? 's' : ''}</span> remaining
              </>
            )}
            {' '}until your trial ends. Payment will be auto-deducted.
          </p>
        </div>
      </div>

      <button
        onClick={handleDismiss}
        className="flex-shrink-0 ml-2 p-1 rounded-full hover:bg-gray-200 transition"
        title="Dismiss"
      >
        <FaTimes className="text-gray-600 text-sm" />
      </button>
    </div>
  );
};
